import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/composables/useApi';
import { clearAllViewState } from '@/composables/useViewState';
import type { AuthUser, AuthTenant } from '@dbx/shared';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<AuthUser | null>(null);
  const currentTenant = ref<AuthTenant | null>(null);
  const availableTenants = ref<AuthTenant[]>([]);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);

  // Single-flight guard for restoreSession() (see below). Declared up here so
  // logout() can reset it.
  let restorePromise: Promise<boolean> | null = null;

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const tenantId = computed(() => currentTenant.value?.id);
  const tenantSlug = computed(() => currentTenant.value?.slug);
  const tenantRole = computed(() => currentTenant.value?.role);
  const isAdmin = computed(() => currentTenant.value?.role === 'admin' || currentTenant.value?.role === 'owner');

  // Actions
  async function login(email: string, password: string, tenantId?: string) {
    const response = await api.auth.login(email, password, tenantId);

    // Store tokens in localStorage FIRST (before reactive state)
    // This ensures localStorage has tokens when watchers fire
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('tenantId', response.tenant.id);

    // Now update reactive state (may trigger watchers)
    accessToken.value = response.accessToken;
    refreshToken.value = response.refreshToken;
    user.value = response.user;
    currentTenant.value = response.tenant;
    availableTenants.value = response.availableTenants;

    return response;
  }

  /**
   * Create an account from an invite and sign in, in one step. The response is
   * login-shaped but scoped to the single workspace the invitee just joined, so
   * availableTenants is seeded with that one tenant.
   */
  async function acceptInvite(token: string, name: string, password: string) {
    const response = await api.invitations.register(token, name, password);

    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('tenantId', response.tenant.id);

    accessToken.value = response.accessToken;
    refreshToken.value = response.refreshToken;
    user.value = response.user;
    currentTenant.value = response.tenant;
    availableTenants.value = [response.tenant];

    return response;
  }

  /**
   * Finish a social sign-in: exchange the single-use handoff code for the
   * token pair and seed session state (login-shaped response, scoped to the
   * user's default workspace).
   */
  async function completeOAuthLogin(code: string) {
    const response = await api.auth.oauthComplete(code);

    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('tenantId', response.tenant.id);

    accessToken.value = response.accessToken;
    refreshToken.value = response.refreshToken;
    user.value = response.user;
    currentTenant.value = response.tenant;
    availableTenants.value = response.availableTenants ?? [response.tenant];

    return response;
  }

  /** Update the signed-in user's display name; reflects in the header live. */
  async function updateName(name: string) {
    const updated = await api.auth.updateProfile(name);
    if (user.value) user.value = { ...user.value, name: updated.name };
    return updated;
  }

  async function logout() {
    if (refreshToken.value) {
      try {
        await api.auth.logout(refreshToken.value);
      } catch {
        // Ignore logout errors
      }
    }

    // Clear state
    user.value = null;
    currentTenant.value = null;
    availableTenants.value = [];
    accessToken.value = null;
    refreshToken.value = null;

    // Clear storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenantId');
    clearAllViewState();

    // Drop any cached restore so a later sign-in can hydrate afresh.
    restorePromise = null;
  }

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      throw new Error('No refresh token');
    }

    const response = await api.auth.refresh(refreshToken.value);
    accessToken.value = response.accessToken;
    localStorage.setItem('accessToken', response.accessToken);

    return response.accessToken;
  }

  async function switchTenant(tenantId: string) {
    const response = await api.auth.switchTenant(tenantId);

    // Persisted filters (job/folder ids etc.) are tenant-specific — drop them.
    clearAllViewState();

    // Store tokens in localStorage FIRST (before reactive state)
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('tenantId', response.tenant.id);

    // Now update reactive state
    accessToken.value = response.accessToken;
    refreshToken.value = response.refreshToken;
    currentTenant.value = response.tenant;

    return response;
  }

  // Single-flight restore. The router guard (main.ts) and App.vue's onMounted
  // both call this during bootstrap, and further navigations can fire while the
  // first /auth/me is still in flight. Sharing one promise means isAuthenticated
  // is never observed half-restored — which is what intermittently bounced
  // deep-linked authed routes (e.g. /documents) to /login.
  async function restoreSession(): Promise<boolean> {
    // Already hydrated (e.g. immediately after login) — nothing to restore.
    if (accessToken.value && user.value) return true;
    return (restorePromise ??= hydrateFromStorage());
  }

  async function hydrateFromStorage(): Promise<boolean> {
    const storedToken = localStorage.getItem('accessToken');
    const storedRefresh = localStorage.getItem('refreshToken');

    if (!storedToken || !storedRefresh) {
      restorePromise = null;
      return false;
    }

    accessToken.value = storedToken;
    refreshToken.value = storedRefresh;

    try {
      const userData = await api.auth.me();
      user.value = userData;

      // Find current tenant from stored ID
      const storedTenantId = localStorage.getItem('tenantId');
      availableTenants.value = userData.tenants;
      currentTenant.value = userData.tenants.find((t: AuthTenant) => t.id === storedTenantId) || userData.tenants[0];

      return true;
    } catch {
      // Token invalid, clear session
      await logout();
      return false;
    }
  }

  return {
    // State
    user,
    currentTenant,
    availableTenants,
    accessToken,
    refreshToken,

    // Getters
    isAuthenticated,
    tenantId,
    tenantSlug,
    tenantRole,
    isAdmin,

    // Actions
    login,
    acceptInvite,
    completeOAuthLogin,
    updateName,
    logout,
    refreshAccessToken,
    switchTenant,
    restoreSession,
  };
});
